import React, { useCallback } from "react";
import { Plus } from "lucide-react";
import ConditionStep from "./ConditionStep";

const FALLBACK_OPTIONS = [
  { value: "role_fallback", label: "Respond in character (default)" },
  { value: "repeat", label: "Ask to repeat" },
  { value: "fail", label: "Mark as failed" },
  { value: "silent", label: "Stay silent" },
];

export default function ConditionalScenarioBuilder({ conditions, fallbackBehavior, onConditionsChange, onFallbackChange }) {
  const groupedByPosition = (conditions || []).reduce((acc, cond) => {
    const pos = cond.position;
    if (!acc[pos]) acc[pos] = [];
    acc[pos].push(cond);
    return acc;
  }, {});

  const positions = Object.keys(groupedByPosition)
    .map(Number)
    .sort((a, b) => a - b);

  const nextId = () => {
    const maxId = (conditions || []).reduce((max, c) => Math.max(max, c.id || 0), 0);
    return maxId + 1;
  };

  const nextPosition = () => {
    return positions.length > 0 ? Math.max(...positions) + 1 : 1;
  };

  const handleAddStep = useCallback(() => {
    const pos = nextPosition();
    const id = nextId();
    const newCondition = {
      id,
      position: pos,
      branch_group: pos,
      match_mode: "sequential",
      condition: "",
      action: "",
      fixed_message: true,
      action_type: "respond",
      action_params: {},
    };
    onConditionsChange([...(conditions || []), newCondition]);
  }, [conditions, onConditionsChange]);

  const handleAddBranch = useCallback((position, branchGroup) => {
    const id = nextId();
    const newBranch = {
      id,
      position,
      branch_group: branchGroup,
      match_mode: "semantic",
      condition: "",
      action: "",
      fixed_message: true,
      action_type: "respond",
      action_params: {},
    };

    const updated = (conditions || []).map((c) => {
      if (c.position === position) {
        return { ...c, match_mode: "semantic" };
      }
      return c;
    });

    onConditionsChange([...updated, newBranch]);
  }, [conditions, onConditionsChange]);

  const handleUpdateCondition = useCallback((updatedStep) => {
    const updated = (conditions || []).map((c) =>
      c.id === updatedStep.id ? updatedStep : c
    );
    onConditionsChange(updated);
  }, [conditions, onConditionsChange]);

  const handleDeleteCondition = useCallback((stepId, position) => {
    let updated = (conditions || []).filter((c) => c.id !== stepId);

    const remainingAtPosition = updated.filter((c) => c.position === position);
    if (remainingAtPosition.length === 1) {
      updated = updated.map((c) => {
        if (c.position === position) {
          return { ...c, match_mode: "sequential" };
        }
        return c;
      });
    }

    const groupedPositions = [...new Set(updated.map((c) => c.position))].sort((a, b) => a - b);
    const positionMap = {};
    groupedPositions.forEach((oldPos, idx) => {
      positionMap[oldPos] = idx + 1;
    });

    updated = updated.map((c) => ({
      ...c,
      position: positionMap[c.position],
      branch_group: positionMap[c.position],
    }));

    onConditionsChange(updated);
  }, [conditions, onConditionsChange]);

  const handleMoveStep = useCallback((position, direction) => {
    const targetPosition = position + direction;
    if (targetPosition < 1 || targetPosition > positions.length) return;

    const updated = (conditions || []).map((c) => {
      if (c.position === position) {
        return { ...c, position: targetPosition, branch_group: targetPosition };
      }
      if (c.position === targetPosition) {
        return { ...c, position: position, branch_group: position };
      }
      return c;
    });

    onConditionsChange(updated);
  }, [conditions, positions, onConditionsChange]);

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-foreground mb-1 block">
          Fallback Behavior
        </label>
        <select
          value={fallbackBehavior || "role_fallback"}
          onChange={(e) => onFallbackChange(e.target.value)}
          className="form-select text-sm"
        >
          {FALLBACK_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          What to do when the bot says something that doesn't match any condition
        </p>
      </div>

      <div className="space-y-3">
        {positions.map((position, posIdx) => {
          const stepsAtPosition = groupedByPosition[position] || [];
          const isMultiBranch = stepsAtPosition.length > 1;

          return (
            <div key={position} className="relative">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-foreground">
                  Step {posIdx + 1}
                </h4>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleMoveStep(position, -1)}
                    disabled={posIdx === 0}
                    className="p-1 text-muted-foreground hover:text-muted-foreground disabled:opacity-30"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveStep(position, 1)}
                    disabled={posIdx === positions.length - 1}
                    className="p-1 text-muted-foreground hover:text-muted-foreground disabled:opacity-30"
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>
              </div>

              <div className={isMultiBranch ? "space-y-2 pl-3 border-l-2 border-violet-200" : ""}>
                {stepsAtPosition.map((step, branchIdx) => (
                  <ConditionStep
                    key={step.id}
                    step={step}
                    branchIndex={isMultiBranch ? branchIdx : undefined}
                    onUpdate={handleUpdateCondition}
                    onDelete={() => handleDeleteCondition(step.id, position)}
                    onAddBranch={() => handleAddBranch(position, step.branch_group)}
                    isOnlyBranch={!isMultiBranch}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleAddStep}
        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-input rounded-lg text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Step
      </button>
    </div>
  );
}
